import React, { useCallback, useEffect, useMemo, useState } from "react";
import axiosInstance from "../../interceptors";
import EmptyState from "../../components/shared/EmptyState";
import LoadingState from "../../components/shared/LoadingState";
import ErrorState from "../../components/shared/ErrorState";

// ── /candidate/resources ───────────────────────────────────────────────────
// Backend: POST /job-posting/api/candidate-portal/resources/list returns
// PrepResourceDTO[] (id, title, description, resourceType, category,
// difficulty, url, estimatedMinutes). Master-only table; the
// MasterDataSourceFilter routes /api/candidate-portal/** to master.
//
// Category filter chips collected dynamically from the returned rows.

const TYPE_ICON = {
  ARTICLE:   "📖",
  VIDEO:     "🎥",
  CHECKLIST: "✅",
  GUIDE:     "🧠",
  TOOL:      "🛠",
  POLICY:    "🛡",
};

const DIFFICULTY_TONE = {
  EASY:   "bg-emerald-100 text-emerald-800",
  MEDIUM: "bg-amber-100 text-amber-800",
  HARD:   "bg-red-100 text-red-800",
  ALL:    "bg-vf-tint text-vf-base",
};

export default function CandidateResources() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorStatus, setErrorStatus] = useState(null);
  const [category, setCategory] = useState("ALL");

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    setErrorStatus(null);
    axiosInstance
      .post("/job-posting/api/candidate-portal/resources/list", {})
      .then((r) => {
        if (r.data?.success) {
          setRows(Array.isArray(r.data.data) ? r.data.data : []);
        } else {
          setError(r.data?.message || "Couldn't load resources");
          setErrorStatus(500);
        }
      })
      .catch((e) => {
        setError(e?.message || "Couldn't load resources");
        setErrorStatus(e?.status || e?.response?.status || 500);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    document.title = "Resources - Ample Hire";
    load();
  }, [load]);

  // Derive category chips from the data so we never show empty buckets.
  const categories = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => { if (r.category) set.add(r.category); });
    return ["ALL", ...Array.from(set).sort()];
  }, [rows]);

  const visible = useMemo(() => {
    if (category === "ALL") return rows;
    return rows.filter((r) => r.category === category);
  }, [rows, category]);

  return (
    <div className="max-w-4xl mx-auto space-y-4">
      <header>
        <h1 className="text-2xl font-semibold text-vf-base m-0">Resources</h1>
        <p className="text-sm text-vf-muted mt-1">
          Guides, checklists, and walkthroughs to help you prepare.
        </p>
      </header>

      {/* Category chips — only render when there's something to filter */}
      {!loading && !error && rows.length > 0 && categories.length > 2 && (
        <div role="tablist" aria-label="Filter resources by category" className="flex flex-wrap gap-2">
          {categories.map((c) => {
            const active = c === category;
            const label = c === "ALL" ? "All" : c;
            return (
              <button
                key={c}
                role="tab"
                type="button"
                aria-selected={active}
                onClick={() => setCategory(c)}
                className={`text-xs px-3 py-1.5 rounded-full border min-h-[36px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-vf-primary ${
                  active
                    ? "bg-vf-primary text-white border-vf-primary"
                    : "bg-white text-vf-base border-subtle hover:bg-vf-tint"
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
      )}

      {/* Body */}
      {loading ? (
        <div className="bg-white border border-subtle rounded-card p-4">
          <LoadingState variant="skeleton" shape="card" count={4} />
        </div>
      ) : error ? (
        <ErrorState
          status={errorStatus}
          description={errorStatus === 403 ? undefined : error}
          retry={load}
        />
      ) : visible.length === 0 ? (
        <div className="bg-white border border-subtle rounded-card">
          <EmptyState
            icon={<span aria-hidden="true">📚</span>}
            title={category === "ALL" ? "No resources yet" : `Nothing in "${category}"`}
            description={
              category === "ALL"
                ? "We're still curating starter guides. Check back soon."
                : "Try another category."
            }
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {visible.map((r) => {
            const icon = TYPE_ICON[r.resourceType] || "📖";
            const diffKey = (r.difficulty || "").toUpperCase();
            const Wrapper = r.url ? "a" : "div";
            const wrapperProps = r.url
              ? { href: r.url, target: "_blank", rel: "noreferrer" }
              : {};
            return (
              <Wrapper
                key={r.id}
                {...wrapperProps}
                className={`block rounded-card border border-subtle bg-white p-4 ${
                  r.url ? "hover:border-vf-primary/40 transition-colors cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-vf-primary" : ""
                }`}
              >
                <div className="text-xl mb-2" aria-hidden="true">{icon}</div>
                <div className="text-sm font-semibold text-vf-base">{r.title}</div>
                {r.description && (
                  <p className="text-xs text-vf-muted mt-1 m-0 leading-relaxed line-clamp-3">
                    {r.description}
                  </p>
                )}
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {r.category && (
                    <span className="text-2xs px-1.5 py-0.5 rounded bg-vf-tint text-vf-base">
                      {r.category}
                    </span>
                  )}
                  {r.difficulty && (
                    <span className={`text-2xs px-1.5 py-0.5 rounded font-semibold ${DIFFICULTY_TONE[diffKey] || DIFFICULTY_TONE.ALL}`}>
                      {r.difficulty}
                    </span>
                  )}
                  {Number.isFinite(r.estimatedMinutes) && r.estimatedMinutes > 0 && (
                    <span className="text-2xs px-1.5 py-0.5 rounded bg-vf-tint text-vf-muted">
                      ⏱ {r.estimatedMinutes} min
                    </span>
                  )}
                  {r.url && (
                    <span className="text-2xs px-1.5 py-0.5 rounded text-vf-primary font-semibold ml-auto">
                      Open →
                    </span>
                  )}
                </div>
              </Wrapper>
            );
          })}
        </div>
      )}
    </div>
  );
}
